import { ageServedValue, sharedCoalescer } from "./requestCoalescer";
import { pollIntervalMs } from "./tickPoll";
import { isFeedFailure, feedErrorMessage } from "./feedError";

/**
 * ONE FEED LEG, WITH EVERY GUARD THIS ESTATE HAS PAID FOR ALREADY ATTACHED.
 *
 * ⛔ EXTRACTED FROM `slice1/Slice1.svelte` BY R765, AND THE EXTRACTION IS THE POINT.
 * R765③ promotes the slice vehicle to the cockpit's primary chart, which gives this logic a
 * SECOND call site. Copying it would have put the cockpit's only chart behind a private copy
 * of three guards that were each written after a real outage — and a copy does not receive the
 * next fix. The guards, and what each one cost:
 *
 *   1. TIMEOUT — measured on the owner's served page 2026-08-23 14:46Z during a REAL outage:
 *      the candles route stopped completing, `Promise.all` never settled, and the page sat on
 *      "loading…" in both themes with no error, no chip, no staleness, indefinitely.
 *      ⇒ ***"NOT YET" IS A STATE A PAGE CAN HOLD FOREVER, AND A SPINNER IS THE QUIETEST WAY
 *      TO FAIL.***
 *   2. ENVELOPE — the read-API answers failure as HTTP 400 with a VALID JSON body
 *      `{ok:false,…}`, and some routes as 200-with-`ok:false`. `fetch` throws on neither and
 *      `res.json()` succeeds on both, so a try/catch cannot see them. Measured on the served
 *      page: a dead candles leg rendered the SUCCESS arm with a blank pane.
 *   3. LOUD — law 1 is TWO things, "a visible STALE/ERROR state AND a log line". The first
 *      draft returned the state and logged nothing: a silent catch wearing a chip.
 *
 * ⚠ `transport` is ONLY a network/parse failure. Envelope judgement belongs to the caller's
 * armed refusals (`admitCvd` and friends) — this function must not become a second opinion
 * about `ok`.
 */

/** Budget for ONE leg. Law 10's price budget is 90s for staleness; a page that has drawn
    NOTHING yet must speak far sooner than that. */
export const LEG_TIMEOUT_MS = 12_000;

export interface LegResult {
  body: unknown;
  /** Non-null ONLY for a network/parse failure. Never an envelope verdict. */
  transport: string | null;
  cacheAgeMs?: number;
}

export async function leg(url: string, tag: string, ttlOverrideMs?: number): Promise<LegResult> {
  const parsed = new URL(url);
  const history = ["before", "from", "to"].some((param) => parsed.searchParams.has(param));
  const ttlMs = ttlOverrideMs ?? (history || tag.startsWith("primary-rung-probe") ? 0 : /\/api\/es\/(ticks|candles|cvd)$/.test(parsed.pathname)
    ? pollIntervalMs() : parsed.pathname === "/api/es/wall-alerts"
      ? 5_000 : /\/api\/es\/vwap-levels$/.test(parsed.pathname)
        ? 60_000 : /\/api\/dealer\/levels$/.test(parsed.pathname)
          ? 180_000 : 0);
  try {
    const hit = await sharedCoalescer.get(url, async () => {
      const result = await requestLeg(url, tag);
      if (result.transport !== null) throw result;
      return result;
    }, ttlMs);
    return { ...hit.value, body: ageServedValue(hit.value.body, hit.cacheAgeMs), cacheAgeMs: hit.cacheAgeMs };
  } catch (error) {
    if (error && typeof error === "object" && "transport" in error) return error as LegResult;
    if (error instanceof Error) {
      const status = "status" in error && typeof error.status === "number" ? error.status : 0;
      console.error(`[${tag}] FEED FAILURE ${new URL(url).pathname} HTTP ${status} — ${error.message}`);
      return { body: null, transport: error.message };
    }
    throw error;
  }
}

async function requestLeg(url: string, tag: string): Promise<LegResult> {
  const loud = (why: string, status: number): LegResult => {
    console.error(`[${tag}] FEED FAILURE ${new URL(url).pathname} HTTP ${status} — ${why}`);
    return { body: null, transport: why };
  };

  const ac = new AbortController();
  const timer = setTimeout(() => ac.abort(), LEG_TIMEOUT_MS);
  try {
    let res: Response;
    try {
      res = await fetch(url, { signal: ac.signal });
    } catch (e) {
      const why =
        (e as Error)?.name === "AbortError"
          ? `no answer within ${LEG_TIMEOUT_MS / 1000}s — the route did not respond`
          : `request failed: ${(e as Error)?.message ?? String(e)}`;
      return loud(why, 0);
    }

    let body: unknown = null;
    try {
      body = await res.json();
    } catch {
      return ac.signal.aborted
        ? loud(`no answer within ${LEG_TIMEOUT_MS / 1000}s — the route did not respond`, 0)
        : loud(feedErrorMessage(null, res.status), res.status);
    }
    const b = body as { ok?: boolean } | null;
    if (isFeedFailure(res.ok, b)) return loud(feedErrorMessage(b, res.status), res.status);
    return { body, transport: null };
  } finally {
    clearTimeout(timer);
  }
}
